import { Rive, Layout, Fit } from "@rive-app/canvas-lite"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

export class CoinsAnimation {
  constructor(canvasId, sectionId, baseSpeed = 0.8, startTrigger = "top 0%", endTrigger = "bottom top") {
    this.canvasId = canvasId
    this.sectionId = sectionId
    this.rive = null
    this.canvas = null
    this.speedProperty = null
    this.baseSpeed = baseSpeed
    this.currentSpeed = baseSpeed
    this.targetSpeed = baseSpeed
    this.lastScrollTime = Date.now()
    this.observer = null
    this.isVisible = false
    this.animationFrameId = null
    this.startTrigger = startTrigger
    this.endTrigger = endTrigger
  }

  init() {
    gsap.registerPlugin(ScrollTrigger)
    this.canvas = document.getElementById(this.canvasId)
    if (!this.canvas) {
      console.warn(`Canvas #${this.canvasId} not found`)
      return false
    }

    this.setupHighDPICanvas()
    this.setupRive()
    this.setupIntersectionObserver()
    return true
  }

  setupHighDPICanvas() {
    this.canvas.style.width = "100%"
    this.canvas.style.height = "100%"
    this.canvas.style.display = "block"
    this.canvas.style.objectFit = "contain"
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting

          if (this.rive) {
            if (entry.isIntersecting) {
              this.rive.play()
            } else {
              this.rive.pause()
            }
          }
        })
      },
      {
        threshold: 0.1,
      }
    )

    this.observer.observe(this.canvas)
  }

  setupRive() {
    this.rive = new Rive({
      src: "https://cdn.prod.website-files.com/68da6f24db5fedbdab2e7d56/68e1788e590408f8a05d1655_threshold_10.riv",
      canvas: this.canvas,
      layoutScaleFactor: 1,
      artboard: "main",
      stateMachines: "stateMachine",
      autoplay: true,
      autoBind: true,
      useOffscreenRenderer: true,
      layout: new Layout({
        fit: Fit.Contain,
        alignment: "center",
      }),
      onLoad: () => {
        this.rive.resizeDrawingSurfaceToCanvas()

        let vmi = this.rive.viewModelInstance
        this.speedProperty = vmi.number("speed")
        this.speedProperty.value = this.baseSpeed

        this.setupScrollTrigger()
        this.startSpeedLerp()
      },
    })
  }

  setupScrollTrigger() {
    ScrollTrigger.create({
      trigger: `#${this.sectionId}`,
      start: this.startTrigger,
      end: this.endTrigger,
      scrub: 1,
      // markers: true,
      onUpdate: (self) => {
        const velocity = self.getVelocity()

        if (Math.abs(velocity) > 10) {
          this.lastScrollTime = Date.now()

          const velocityMultiplier = 0.01
          this.targetSpeed = this.baseSpeed + (velocity * velocityMultiplier)

          const minSpeed = -3
          const maxSpeed = 3
          this.targetSpeed = Math.max(minSpeed, Math.min(maxSpeed, this.targetSpeed))
        }
      },
    })
  }

  startSpeedLerp() {
    const updateSpeed = () => {
      if (!this.speedProperty || !this.isVisible) {
        this.animationFrameId = requestAnimationFrame(updateSpeed)
        return
      }

      const timeSinceLastScroll = Date.now() - this.lastScrollTime
      const idleThreshold = 100

      if (timeSinceLastScroll > idleThreshold) {
        this.targetSpeed = this.baseSpeed
      }

      const lerpFactor = 0.1
      this.currentSpeed += (this.targetSpeed - this.currentSpeed) * lerpFactor

      this.speedProperty.value = this.currentSpeed

      this.animationFrameId = requestAnimationFrame(updateSpeed)
    }

    updateSpeed()
  }

  destroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
    }

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    if (this.rive) {
      this.rive.cleanup()
      this.rive = null
    }

    ScrollTrigger.getAll().forEach((st) => {
      if (st.vars.trigger === `#${this.sectionId}`) {
        st.kill()
      }
    })
  }
}